import { IRoleType } from '../services/user-details/user-details.model';
import { IconName } from '../interfaces/icon-picker.model';

export interface INavButton {
  to: string;
  icon: IconName;
  activeIcon: IconName;
  name: string;
  isActive: (path: string) => boolean;
  allowedRoles: IRoleType[]
}

export const navArray: INavButton[] = [
  {
    name: 'Dashboard',
    to: '/dashboard',
    icon: IconName.SpaceDashboardOutlined,
    activeIcon: IconName.SpaceDashboard,
    isActive: (path) => path === '/dashboard',
    allowedRoles: ['admin', 'analyst', 'client', 'data-owner']
  },
  {
    name: 'My Tasks',
    to: '/my-tasks',
    icon: IconName.AssignmentOutlined,
    activeIcon: IconName.Assignment,
    isActive: (path) => path.startsWith('/my-tasks'),
    allowedRoles: ['data-owner']
  },
  {
    name: 'Organisation',
    to: '/organisation/locations',
    icon: IconName.AccountBalanceOutlined,
    activeIcon: IconName.AccountBalance,
    isActive: (path) => path.startsWith('/organisation'),
    allowedRoles: ['admin', 'analyst']
  },
  {
    name: 'Emissions',
    to: '/emissions/overview',
    icon: IconName.FilterDrama,
    activeIcon: IconName.Cloud,
    isActive: (path) => path.startsWith('/emissions'),
    allowedRoles: ['admin', 'analyst', 'data-owner']
  },
  {
    name: 'Surveys',
    to: '/surveys',
    // Ballot pair: Poll read as a mini bar chart two slots from the Analytics one, and
    // Assignment is already My Tasks. The ballot sheet reads as a questionnaire.
    icon: IconName.BallotOutlined,
    activeIcon: IconName.Ballot,
    isActive: (path) => path.startsWith('/surveys'),
    allowedRoles: ['admin', 'analyst']
  },
  {
    name: 'Product Carbon Footprint',
    to: '/products',
    icon: IconName.Inventory2Outlined,
    activeIcon: IconName.Inventory2,
    isActive: (path) => path.startsWith('/products'),
    allowedRoles: ['admin', 'analyst']
  },
  {
    name: 'Reduction Plan',
    to: '/reduction-plan/targets',
    icon: IconName.TrendingDown,
    activeIcon: IconName.TrendingDown,
    isActive: (path) => path.startsWith('/reduction-plan'),
    allowedRoles: ['admin', 'analyst']
  },
  {
    name: 'Analytics',
    to: '/analytics/dashboard',
    icon: IconName.StackedBarChartOutlined,
    activeIcon: IconName.StackedBarChart,
    isActive: (path) => path.startsWith('/analytics'),
    allowedRoles: ['admin', 'analyst', 'client']
  },
  {
    name: 'Report Readiness',
    to: '/readiness',
    icon: IconName.FactCheckOutlined,
    activeIcon: IconName.FactCheck,
    isActive: (path) => path.startsWith('/readiness'),
    allowedRoles: ['admin', 'analyst']
  },
  {
    name: 'Regulatory Reporting',
    to: '/reporting',
    icon: IconName.DescriptionOutlined,
    activeIcon: IconName.Description,
    isActive: (path) => path.startsWith('/reporting'),
    allowedRoles: ['admin', 'analyst']
  }
]

export const partnerArray: INavButton[] = [
  {
    name: 'Clients',
    to: '/partner-clients',
    icon: IconName.AccountBalanceOutlined,
    activeIcon: IconName.AccountBalance,
    isActive: (path) => path.startsWith('/partner-clients'),
    allowedRoles: ['admin', 'analyst', 'client']
  },
  {
    // Points at /partner-library/rules rather than /partner-library so surveys can be added
    // as a sibling in phase 2 without any URL changing.
    name: 'Library',
    to: '/partner-library/rules',
    icon: IconName.MenuBookOutlined,
    activeIcon: IconName.MenuBook,
    isActive: (path) => path.startsWith('/partner-library'),
    allowedRoles: ['admin', 'analyst', 'client']
  },
]

export const superAdminArray: INavButton[] = [
  {
    name: 'Super Admin',
    to: '/super-admin',
    icon: IconName.AdminPanelSettingsOutlined,
    activeIcon: IconName.AdminPanelSettings,
    isActive: (path) => path === '/super-admin',
    allowedRoles: ['admin']
  }
]

export interface ILeftInnerNavPath {
  name: string;
  to: string;
  isActive: (path: string) => boolean
  // Optional per-path narrowing; when absent, the section's allowedRoles decide.
  allowedRoles?: IRoleType[]
}

export interface ILeftInnerNav {
  category: string;
  subcategory: ILeftInnerNavPath[]
  allowedRoles: IRoleType[]
}

export const settingsArray: ILeftInnerNav[] = [
  {
    category: 'Personal Settings',
    subcategory: [
      {
        name: 'Your Profile',
        to: '/settings/personal-settings/your-profile',
        isActive: (path) => path === '/settings/personal-settings/your-profile',
      },
      {
        name: 'Home Page',
        to: '/settings/personal-settings/home-page',
        isActive: (path) => path === '/settings/personal-settings/home-page',
      },
      {
        name: 'Two-factor auth',
        to: '/settings/personal-settings/two-factor-authentication',
        isActive: (path) => path === '/settings/personal-settings/two-factor-authentication',
      }
    ],
    allowedRoles: ['admin', 'analyst', 'client', 'data-owner']
  },
  {
    category: 'Company Settings',
    subcategory: [
      {
        name: 'Overview',
        to: '/settings/company-settings/overview',
        isActive: (path) => path === '/settings/company-settings/overview',
      },
      {
        name: 'Users',
        to: '/settings/company-settings/users',
        isActive: (path) => path.startsWith('/settings/company-settings/users'),
      },
      {
        name: 'Distribution Lists',
        to: '/settings/company-settings/distribution-lists',
        isActive: (path) => path.startsWith('/settings/company-settings/distribution-lists'),
      },
      {
        name: 'Reporting Periods',
        to: '/settings/company-settings/reporting-periods',
        isActive: (path) => path.startsWith('/settings/company-settings/reporting-periods'),
      },
      {
        name: 'Conversion Factors',
        to: '/settings/company-settings/conversion-factors',
        isActive: (path) => path.startsWith('/settings/company-settings/conversion-factors'),
      },
      {
        name: 'Intensity Ratios',
        to: '/settings/company-settings/intensity-ratios',
        isActive: (path) => path.startsWith('/settings/company-settings/intensity-ratios'),
      },
      {
        name: 'Custom Fields',
        to: '/settings/company-settings/custom-fields',
        isActive: (path) => path.startsWith('/settings/company-settings/custom-fields'),
      },
      {
        name: 'Product Categories',
        to: '/settings/company-settings/product-categories',
        isActive: (path) => path.startsWith('/settings/company-settings/product-categories'),
      },
      {
        name: 'Downloaded Files',
        to: '/settings/company-settings/downloaded-files',
        isActive: (path) => path.startsWith('/settings/company-settings/downloaded-files'),
      },
      {
        name: 'Schedules',
        to: '/settings/company-settings/schedules',
        isActive: (path) => path.startsWith('/settings/company-settings/schedules'),
      },
    ],
    allowedRoles: ['admin']
  },
  {
    category: 'Display Settings',
    subcategory: [
      {
        name: 'Custom Screen Labels',
        to: '/settings/company-settings/custom-screen-labels',
        isActive: (path) => path.startsWith('/settings/company-settings/custom-screen-labels'),
      },
      {
        name: 'Input Field Validation',
        to: '/settings/company-settings/input-fields',
        isActive: (path) => path.startsWith('/settings/company-settings/input-fields'),
      },
    ],
    allowedRoles: ['admin']
  },
  {
    category: 'Admin settings',
    subcategory: [
      {
        name: 'Two-factor auth',
        to: '/settings/admin-settings/two-factor',
        isActive: (path) => path.startsWith('/settings/admin-settings/two-factor'),
      },
      {
        name: 'Single sign-on',
        to: '/settings/admin-settings/single-sign-on',
        isActive: (path) => path.startsWith('/settings/admin-settings/single-sign-on'),
      },
      {
        name: 'Integrations',
        to: '/settings/integrations',
        isActive: (path) => path.startsWith('/settings/integrations'),
      },
      {
        name: 'API Keys',
        to: '/settings/company-settings/api-keys',
        isActive: (path) => path.startsWith('/settings/company-settings/api-keys'),
      },
    ],
    allowedRoles: ['admin']
  },
  {
    category: 'Data import',
    subcategory: [
      {
        name: 'Import Data',
        to: '/settings/data-upload/import-data',
        isActive: (path) => path.startsWith('/settings/data-upload/import-data'),
      },
      {
        name: 'Custom Rules',
        to: '/settings/data-upload/custom-rules',
        isActive: (path) => path.startsWith('/settings/data-upload/custom-rules'),
      },
      {
        name: 'Chart of Accounts',
        to: '/settings/data-upload/chart-of-accounts',
        isActive: (path) => path.startsWith('/settings/data-upload/chart-of-accounts'),
      },
      {
        name: 'Import History',
        to: '/settings/data-upload/import-history',
        isActive: (path) => path.startsWith('/settings/data-upload/import-history'),
      }
    ],
    // Analysts can import too: the BE file-upload routes accept admin and analyst
    // since the ux-be-followups role widening.
    allowedRoles: ['admin', 'analyst']
  },
  {
    category: 'Logs',
    subcategory: [
      {
        name: 'User Action Log',
        to: '/settings/company-settings/user-action-log',
        isActive: (path) => path.startsWith('/settings/company-settings/user-action-log'),
      },
    ],
    allowedRoles: ['admin']
  }
]

export const organisationArray: ILeftInnerNav[] = [
  {
    category: 'Organisation',
    subcategory: [
      {
        name: 'Subsidiaries',
        to: '/organisation/subsidiaries',
        isActive: (path) => path.startsWith('/organisation/subsidiaries'),
      },
      {
        name: 'Locations',
        to: '/organisation/locations',
        isActive: (path) => path.startsWith('/organisation/locations'),
      },
      {
        name: 'Vehicles',
        to: '/organisation/vehicles',
        isActive: (path) => path.startsWith('/organisation/vehicles'),
      },
      {
        name: 'Suppliers',
        to: '/organisation/suppliers',
        isActive: (path) => path.startsWith('/organisation/suppliers'),
      },
      {
        // Split from the single "Goods" entry: the register holds two kinds of record
        // (purchased & capital goods, and products the organisation sells) and one page
        // with a toggle hid that. Orgs with a custom "product" screen label see their own
        // term folded in (see inner-left-navbar and mobile-navbar).
        name: 'Purchased & Capital Goods',
        to: '/organisation/products',
        isActive: (path) => path.startsWith('/organisation/products'),
      },
      {
        name: 'Sold Products',
        to: '/organisation/sold-products',
        isActive: (path) => path.startsWith('/organisation/sold-products'),
      },
      {
        name: 'Projects',
        to: '/organisation/projects',
        isActive: (path) => path.startsWith('/organisation/projects'),
      },
      {
        name: 'Renewable Energy Certificates',
        to: '/organisation/renewable-energy-certificates',
        isActive: (path) => path.startsWith('/organisation/renewable-energy-certificates'),
      },

    ],
    allowedRoles: ['admin', 'analyst']
  }
]

export const analyticsArray: ILeftInnerNav[] = [
  {
    category: 'Analytics',
    subcategory: [
      {
        name: 'Dashboard',
        to: '/analytics/dashboard',
        // Excludes the custom-dashboards path (which starts with a different segment) so only
        // one entry highlights at a time.
        isActive: (path) => path.startsWith('/analytics/dashboard') && !path.startsWith('/analytics/custom-dashboards'),
      },
      {
        name: 'Custom Dashboards',
        to: '/analytics/custom-dashboards',
        isActive: (path) => path.startsWith('/analytics/custom-dashboards'),
      },
      // {
      //   name: 'Emissions Breakdown',
      //   to: '/analytics/breakdown',
      //   isActive: (path) => path.startsWith('/analytics/breakdown'),
      // },
      {
        // "Saved Reports" to stop the collision with Regulatory Reporting > Reports.
        name: 'Saved Reports',
        to: '/analytics/reports',
        isActive: (path) => path.startsWith('/analytics/reports'),
      },
      {
        name: 'Hotspot Analysis',
        to: '/analytics/hotspots',
        isActive: (path) => path.startsWith('/analytics/hotspots'),
      },
    ],
    allowedRoles: ['admin', 'analyst', 'client']
  }
]

export const reportingArray: ILeftInnerNav[] = [
  {
    category: 'Regulatory Reporting',
    allowedRoles: ['admin', 'analyst'],
    subcategory: [
      {
        name: 'Reports',
        to: '/reporting',
        // The report hub and every report flow beneath it, except the pages with their own entry.
        isActive: (path) => path.startsWith('/reporting')
          && !path.startsWith('/reporting/regulations'),
      },
      {
        name: 'Regulations',
        to: '/reporting/regulations',
        isActive: (path) => path.startsWith('/reporting/regulations'),
      },
    ],
  },
]

export const emissionsArray: ILeftInnerNav[] = [
  {
    category: 'Emissions',
    subcategory: [
      {
        name: 'Overview',
        to: '/emissions/overview',
        isActive: (path) => path.startsWith('/emissions/overview'),
      },
      {
        // Matches the page's own title; "Financial" said nothing about what the page does.
        name: 'Spend Based Emissions',
        to: '/emissions/financial',
        isActive: (path) => path.startsWith('/emissions/financial'),
        // Ledger review and bulk import stay analyst/admin; a data owner enters activity data
        // directly on the Overview page.
        allowedRoles: ['admin', 'analyst'],
      },
      {
        name: 'Import Emissions',
        // context=emissions keeps this (Emissions) menu instead of the Settings menu across the import
        // flow; dataType=emissions&start=upload defaults the wizard straight to the emissions upload.
        to: '/settings/data-upload/import-data?dataType=emissions&start=upload&context=emissions',
        isActive: (path) => path.startsWith('/settings/data-upload/import-data') || path.startsWith('/settings/data-upload/commit-progress'),
        allowedRoles: ['admin', 'analyst'],
      },
      // The legacy data-requests landing (/emissions/surveys) is deliberately NOT in the nav:
      // its employee half is superseded by the Surveys module, and supplier engagement now runs
      // through the survey engine too. The pages stay reachable by direct URL until the product
      // data-request write-back moves into the new engine and the legacy area is retired.
    ],
    allowedRoles: ['admin', 'analyst', 'data-owner']
  }
]

export const surveysArray: ILeftInnerNav[] = [
  {
    category: 'Survey Management',
    subcategory: [
      {
        name: 'Surveys',
        to: '/surveys',
        isActive: (path) => path === '/surveys' || (path.startsWith('/surveys/') && !path.startsWith('/surveys/library') && !path.startsWith('/surveys/questions') && !path.startsWith('/surveys/question-groups')),
      },
      {
        // Questions and question sets in one demoted power-user area; everyday
        // survey building happens on the survey hub itself.
        name: 'Question Library',
        to: '/surveys/library',
        isActive: (path) => path.startsWith('/surveys/library') || path.startsWith('/surveys/questions') || path.startsWith('/surveys/question-groups'),
      },
    ],
    allowedRoles: ['admin', 'analyst'],
  },
]

// The partner Library's second-level rail.
//
// allowedRoles must stay all three: the inner rail filters each section by role, whereas the
// outer sidebar renders partnerArray without any role check. A partner user whose only role
// is 'client' would otherwise see the Library icon and an empty rail behind it.
export const partnerLibraryArray: ILeftInnerNav[] = [
  {
    category: 'Library',
    subcategory: [
      {
        name: 'Rules',
        to: '/partner-library/rules',
        isActive: (path) => path.startsWith('/partner-library/rules'),
      },
      {
        name: 'Analytic Reports',
        to: '/partner-library/analytic-reports',
        isActive: (path) => path.startsWith('/partner-library/analytic-reports'),
      },
      {
        name: 'Surveys',
        to: '/partner-library/surveys',
        isActive: (path) => path.startsWith('/partner-library/surveys'),
      },
    ],
    allowedRoles: ['admin', 'analyst', 'client'],
  },
]

// The partner settings area (reached from the top-bar gear). The partner's own
// organisation settings live under "Settings"; two-factor and single sign-on for
// that same organisation live under "Security". Client SSO/2FA is managed from
// the client's own account, not here.
export const partnerSettingsArray: ILeftInnerNav[] = [
  {
    category: 'Settings',
    subcategory: [
      {
        name: 'Overview',
        to: '/partner-settings/overview',
        isActive: (path) => path.startsWith('/partner-settings/overview'),
      },
    ],
    allowedRoles: ['admin', 'analyst', 'client'],
  },
  {
    category: 'Security',
    subcategory: [
      {
        name: 'Two-factor auth',
        to: '/partner-settings/security/two-factor',
        isActive: (path) => path.startsWith('/partner-settings/security/two-factor'),
      },
      {
        name: 'Single sign-on',
        to: '/partner-settings/security/single-sign-on',
        isActive: (path) => path.startsWith('/partner-settings/security/single-sign-on'),
      },
    ],
    allowedRoles: ['admin', 'analyst', 'client'],
  },
]

export const reductionArray: ILeftInnerNav[] = [
  {
    category: 'Reduction Plan',
    subcategory: [
      {
        name: 'Targets',
        to: '/reduction-plan/targets',
        isActive: (path) => path.startsWith('/reduction-plan/targets'),
      },
      {
        name: 'Actions',
        to: '/reduction-plan/actions',
        isActive: (path) => path.startsWith('/reduction-plan/actions'),
      },
      {
        name: 'Scenarios',
        to: '/reduction-plan/scenarios',
        isActive: (path) => path.startsWith('/reduction-plan/scenarios'),
      },
      {
        name: 'FLAG Removals',
        to: '/reduction-plan/flag',
        isActive: (path) => path.startsWith('/reduction-plan/flag'),
      },
      {
        name: 'Offsets',
        to: '/reduction-plan/offsets',
        isActive: (path) => path.startsWith('/reduction-plan/offsets'),
      },
    ],
    allowedRoles: ['admin', 'analyst']
  }
]